import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Key, Shield, ShieldCheck, User as UserIcon, Search, X, Check, SlidersHorizontal, Fingerprint, Wand2, KeyRound, Globe } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

const roleBadge = (r) => ({
    admin: "bg-[#E53935]/10 text-[#E53935]",
    board: "bg-blue-100 text-blue-700",
    user: "bg-gray-100 text-gray-600",
}[r] || "bg-gray-100 text-gray-600");

const roleIcon = (r) => ({
    admin: ShieldCheck,
    board: Shield,
    user: UserIcon,
}[r] || UserIcon);

// Sign-in method chip — visualizes what auth mechanism a user is actually
// USING (based on their last successful login event). Admin can glance at
// the column and see who to nudge toward WebAuthn / passkeys.
const SIGNIN_METHOD_META = {
    passkey:    { label: "Passkey",    icon: Fingerprint, style: "bg-emerald-100 text-emerald-800" },
    magic_link: { label: "Magic link", icon: Wand2,       style: "bg-purple-100 text-purple-700"  },
    password:   { label: "Password",   icon: KeyRound,    style: "bg-amber-100 text-amber-800"    },
    google:     { label: "Google",     icon: Globe,       style: "bg-blue-100 text-blue-700"      },
    facebook:   { label: "Facebook",   icon: Globe,       style: "bg-blue-100 text-blue-700"      },
    signup:     { label: "New — never signed in", icon: UserIcon, style: "bg-gray-100 text-gray-600" },
    never:      { label: "Never",      icon: UserIcon,    style: "bg-gray-100 text-gray-500"      },
};

const SigninMethodChip = ({ method, at, testid }) => {
    const meta = SIGNIN_METHOD_META[method] || SIGNIN_METHOD_META.never;
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.style}`}
            data-testid={testid}
            data-signin-method={method}
            title={at ? `Last used ${new Date(at).toLocaleString()}` : "No sign-in on record"}
        >
            <Icon className="w-2.5 h-2.5" strokeWidth={2.5} /> {meta.label}
        </span>
    );
};

export const UsersPanel = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [roleFilter, setRoleFilter] = useState("");
    const [q, setQ] = useState("");
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pwResetId, setPwResetId] = useState(null);
    const [form, setForm] = useState({ email: "", name: "", role: "user", password: "" });
    const [editForm, setEditForm] = useState({ name: "", role: "user", active: true });
    const [pwForm, setPwForm] = useState({ new_password: "", confirm: "" });

    // Permissions modal state
    const [permsUserId, setPermsUserId] = useState(null);
    const [permsCatalog, setPermsCatalog] = useState(null);
    const [permsSelected, setPermsSelected] = useState([]);
    const [permsLoading, setPermsLoading] = useState(false);
    const [permsAudit, setPermsAudit] = useState([]);
    const [auditOpen, setAuditOpen] = useState(false);

    const loadAudit = async (uid) => {
        try {
            const { data } = await api.get(`/admin/permissions/audit?user_id=${uid}&limit=50`);
            setPermsAudit(data.entries || []);
        } catch (e) {
            setPermsAudit([]);
        }
    };

    const openPerms = async (u) => {
        setPermsUserId(u.user_id);
        setPermsSelected(Array.isArray(u.permissions) ? u.permissions : []);
        setAuditOpen(false);
        setPermsAudit([]);
        if (!permsCatalog) {
            try {
                const { data } = await api.get("/admin/permissions/catalog");
                setPermsCatalog(data);
                // Fallback default when user has no explicit permissions yet
                if (!u.permissions) setPermsSelected(data.default_permissions || []);
            } catch (e) {
                toast.error("Could not load permissions catalog");
            }
        } else if (!u.permissions) {
            setPermsSelected(permsCatalog.default_permissions || []);
        }
        loadAudit(u.user_id);
    };

    const togglePerm = (key) => {
        setPermsSelected((curr) => curr.includes(key) ? curr.filter((k) => k !== key) : [...curr, key]);
    };

    const applyPreset = (slug) => {
        if (!permsCatalog) return;
        const preset = (permsCatalog.presets || []).find((p) => p.slug === slug);
        if (preset) setPermsSelected(preset.permissions || []);
    };

    const savePerms = async () => {
        setPermsLoading(true);
        try {
            await api.patch(`/admin/users/${permsUserId}/permissions`, { permissions: permsSelected });
            toast.success("Permissions updated");
            await loadAudit(permsUserId);
            setPermsUserId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setPermsLoading(false);
        }
    };

    const load = async () => {
        try {
            const params = {};
            if (roleFilter) params.role = roleFilter;
            if (q) params.q = q;
            const [u, s] = await Promise.all([
                api.get("/admin/users", { params }),
                api.get("/admin/users/stats"),
            ]);
            setUsers(u.data || []);
            setStats(s.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load users");
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);

    const submitNew = async () => {
        if (!form.email || !form.name) { toast.error("Email and name required"); return; }
        if ((form.role === "admin" || form.role === "board") && form.password.length < 8) {
            toast.error("Admin/Board passwords must be at least 8 characters");
            return;
        }
        try {
            await api.post("/admin/users", form);
            toast.success("User created");
            setAdding(false);
            setForm({ email: "", name: "", role: "user", password: "" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const startEdit = (u) => {
        setEditingId(u.user_id);
        setEditForm({ name: u.name, role: u.role, active: u.active !== false });
    };
    const saveEdit = async () => {
        try {
            await api.patch(`/admin/users/${editingId}`, editForm);
            toast.success("Updated");
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const submitReset = async () => {
        if (pwForm.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
        try {
            await api.post(`/admin/users/${pwResetId}/reset-password`, { new_password: pwForm.new_password });
            toast.success("Password reset — share it with the user securely");
            setPwResetId(null);
            setPwForm({ new_password: "", confirm: "" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        }
    };

    const del = async (u) => {
        if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
        try { await api.delete(`/admin/users/${u.user_id}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div data-testid="admin-users-panel" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">User management</h2>
                    <p className="text-xs text-[#4A5568] mt-1">All accounts — admins, board members, and customers. Create staff users, manage roles, reset passwords.</p>
                </div>
                <button onClick={() => setAdding(true)} data-testid="user-new-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Invite user
                </button>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="user-stats">
                    <StatCard label="Total" value={stats.total} color="bg-[#F4F1EA] text-[#1C252E]" />
                    <StatCard label="Admins" value={stats.admin} color="bg-[#E53935]/10 text-[#E53935]" testid="user-stat-admin" />
                    <StatCard label="Board" value={stats.board} color="bg-blue-100 text-blue-700" testid="user-stat-board" />
                    <StatCard label="Customers" value={stats.user} color="bg-gray-100 text-gray-700" testid="user-stat-user" />
                    <StatCard label="Last 30d" value={stats.recent_signups_30d} color="bg-green-100 text-green-700" />
                </div>
            )}

            {/* Sign-in method rollup — shows the mix across the user base so the admin
                can see at a glance who's using passwordless (magic-link/passkey) vs
                still on passwords. Counts are computed client-side from the currently
                loaded users list so search/role filters flow through automatically. */}
            {users.length > 0 && (
                <SigninMethodSummary users={users} />
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search email or name…"
                        data-testid="user-search"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-black/10 text-sm bg-white"
                    />
                </div>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    data-testid="user-role-filter"
                    className="rounded-xl border border-black/10 px-4 py-2.5 text-sm bg-white"
                >
                    <option value="">All roles</option>
                    <option value="admin">Admins</option>
                    <option value="board">Board</option>
                    <option value="user">Customers</option>
                </select>
                <button onClick={load} className="text-sm text-[#E53935] hover:underline">Refresh</button>
            </div>

            {/* Mobile (<sm): stacked cards */}
            <div className="sm:hidden space-y-3" data-testid="user-list-mobile">
                {users.length === 0 ? (
                    <p className="text-center text-[#4A5568] py-8 bg-white border border-black/5 rounded-2xl">No users found.</p>
                ) : users.map((u) => {
                    const RoleIcon = roleIcon(u.role);
                    const isSelf = u.email === currentUser?.email;
                    return (
                        <div key={u.user_id} className="bg-white border border-black/5 rounded-2xl p-4" data-testid={`user-card-${u.user_id}`}>
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-[#F4F1EA] flex items-center justify-center text-xs font-bold text-[#1C252E] flex-shrink-0">
                                    {(u.name || u.email).slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-[#1C252E] truncate">
                                        {u.name || "—"}{isSelf && <span className="ml-1 text-[10px] text-[#E53935] font-bold uppercase tracking-wider">(you)</span>}
                                    </p>
                                    <p className="text-xs text-[#4A5568] truncate">{u.email}</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${roleBadge(u.role)}`}>
                                    <RoleIcon className="w-3 h-3" strokeWidth={2} /> {u.role}
                                </span>
                                {u.is_protected && <span className="text-[10px] text-amber-700" title="Seeded — protected">🔒</span>}
                                {u.active === false
                                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-bold uppercase tracking-wider">Inactive</span>
                                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider">Active</span>}
                                {u.webauthn_count > 0 ? (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider"
                                        data-testid={`user-webauthn-on-${u.user_id}`}
                                        title={`${u.webauthn_count} security key(s) / passkey(s) enrolled`}
                                    >
                                        <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} /> Key
                                    </span>
                                ) : ["admin", "board"].includes(u.role) ? (
                                    <span
                                        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider"
                                        data-testid={`user-webauthn-missing-${u.user_id}`}
                                        title="No security key enrolled — recommended for admin/board accounts"
                                    >
                                        No key
                                    </span>
                                ) : null}
                                <SigninMethodChip
                                    method={u.last_signin_method || "never"}
                                    at={u.last_signin_at}
                                    testid={`user-signin-method-${u.user_id}`}
                                />
                                <span className="text-[10px] text-[#4A5568] uppercase tracking-wider">{u.provider || "local"}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2 flex-wrap pt-3 border-t border-black/5">
                                <div className="text-[10px] text-[#4A5568]">
                                    <span>Joined {fmtDate(u.created_at)}</span>
                                    {u.last_login && <span className="ml-2">· Last {fmtDate(u.last_login)}</span>}
                                </div>
                                <div className="inline-flex items-center gap-1">
                                    <button onClick={() => startEdit(u)} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Edit">
                                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    {u.role === "board" && (
                                        <button onClick={() => openPerms(u)} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Manage permissions">
                                            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    )}
                                    {u.provider === "local" && (
                                        <button onClick={() => { setPwResetId(u.user_id); setPwForm({ new_password: "", confirm: "" }); }} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Reset password">
                                            <Key className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    )}
                                    <button onClick={() => del(u)} disabled={u.is_protected || isSelf} className="p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Delete">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Desktop (≥sm): traditional table */}
            <div className="hidden sm:block overflow-x-auto bg-white border border-black/5 rounded-2xl">
                <table className="w-full text-sm" data-testid="user-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr>
                            <th className="px-4 py-3 text-left">User</th>
                            <th className="px-4 py-3 text-left">Role</th>
                            <th className="px-4 py-3 text-left">Sign-in</th>
                            <th className="px-4 py-3 text-left">Provider</th>
                            <th className="px-4 py-3 text-left">Status</th>
                            <th className="px-4 py-3 text-left">Joined</th>
                            <th className="px-4 py-3 text-left">Last login</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {users.length === 0 && (
                            <tr><td colSpan={8} className="px-4 py-10 text-center text-[#4A5568]">No users found.</td></tr>
                        )}
                        {users.map((u) => {
                            const RoleIcon = roleIcon(u.role);
                            const isSelf = u.email === currentUser?.email;
                            return (
                                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-[#F4F1EA]/40">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-[#F4F1EA] flex items-center justify-center text-xs font-bold text-[#1C252E] flex-shrink-0">
                                                {(u.name || u.email).slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-[#1C252E] truncate">
                                                    {u.name || "—"}{isSelf && <span className="ml-1 text-[10px] text-[#E53935] font-bold uppercase tracking-wider">(you)</span>}
                                                </p>
                                                <p className="text-xs text-[#4A5568] truncate">{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${roleBadge(u.role)}`} data-testid={`user-role-${u.user_id}`}>
                                            <RoleIcon className="w-3 h-3" strokeWidth={2} /> {u.role}
                                        </span>
                                        {u.is_protected && <span className="ml-1 text-[10px] text-amber-700" title="Seeded — protected from deletion/role change">🔒</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <SigninMethodChip
                                            method={u.last_signin_method || "never"}
                                            at={u.last_signin_at}
                                            testid={`user-signin-method-${u.user_id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{u.provider || "local"}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {u.active === false
                                                ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 font-bold uppercase tracking-wider">Inactive</span>
                                                : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider">Active</span>}
                                            {u.webauthn_count > 0 ? (
                                                <span
                                                    className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider"
                                                    data-testid={`user-webauthn-on-${u.user_id}`}
                                                    title={`${u.webauthn_count} security key(s) / passkey(s) enrolled`}
                                                >
                                                    <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} /> Key
                                                </span>
                                            ) : ["admin", "board"].includes(u.role) ? (
                                                <span
                                                    className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wider"
                                                    data-testid={`user-webauthn-missing-${u.user_id}`}
                                                    title="No security key enrolled — recommended for admin/board accounts"
                                                >
                                                    No key
                                                </span>
                                            ) : null}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{fmtDate(u.created_at)}</td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{fmtDate(u.last_login)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => startEdit(u)} data-testid={`user-edit-${u.user_id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Edit">
                                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                            {u.role === "board" && (
                                                <button onClick={() => openPerms(u)} data-testid={`user-perms-${u.user_id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Manage permissions" title="Manage board permissions">
                                                    <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
                                                </button>
                                            )}
                                            {u.provider === "local" && (
                                                <button onClick={() => { setPwResetId(u.user_id); setPwForm({ new_password: "", confirm: "" }); }} data-testid={`user-pw-${u.user_id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Reset password">
                                                    <Key className="w-4 h-4" strokeWidth={1.5} />
                                                </button>
                                            )}
                                            <button onClick={() => del(u)} disabled={u.is_protected || isSelf} data-testid={`user-del-${u.user_id}`} className="p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA] disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Delete">
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {adding && (
                <Modal onClose={() => setAdding(false)} title="Invite a new user" testid="user-create-modal">
                    <div className="space-y-3">
                        <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-form-role" className="w-full rounded-xl border border-black/10 px-4 py-3">
                            <option value="user">Customer (no admin access)</option>
                            <option value="board">Board member (approve spend requests)</option>
                            <option value="admin">Admin (full access)</option>
                        </select>
                        {(form.role === "admin" || form.role === "board") && (
                            <input type="password" placeholder="Initial password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        )}
                        {form.role === "user" && (
                            <p className="text-xs text-[#4A5568]">Customer accounts sign in via Google or Facebook — no password needed here. They will appear in this list after their first sign-in if you&apos;d prefer to wait.</p>
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-5">
                        <button onClick={() => setAdding(false)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                        <button onClick={submitNew} data-testid="user-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Create user</button>
                    </div>
                </Modal>
            )}

            {editingId && (
                <Modal onClose={() => setEditingId(null)} title="Edit user" testid="user-edit-modal">
                    <div className="space-y-3">
                        <input placeholder="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="user-edit-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} data-testid="user-edit-role" className="w-full rounded-xl border border-black/10 px-4 py-3">
                            <option value="user">Customer</option>
                            <option value="board">Board member</option>
                            <option value="admin">Admin</option>
                        </select>
                        <label className="flex items-center gap-2 text-sm text-[#1C252E] cursor-pointer">
                            <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} data-testid="user-edit-active" />
                            <span>Active (uncheck to disable login)</span>
                        </label>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-5">
                        <button onClick={() => setEditingId(null)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                        <button onClick={saveEdit} data-testid="user-edit-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-1">
                            <Check className="w-4 h-4" /> Save
                        </button>
                    </div>
                </Modal>
            )}

            {pwResetId && (
                <Modal onClose={() => setPwResetId(null)} title="Reset password" testid="user-pw-modal">
                    <p className="text-sm text-[#4A5568] mb-4">Set a new password. The user will be unlocked if they were locked out. Share the new password with them through a secure channel.</p>
                    <div className="space-y-3">
                        <input type="password" placeholder="New password (min 8 chars)" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="user-pw-new" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                        <input type="password" placeholder="Confirm password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="user-pw-confirm" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-5">
                        <button onClick={() => setPwResetId(null)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                        <button onClick={submitReset} data-testid="user-pw-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">Reset password</button>
                    </div>
                </Modal>
            )}

            {permsUserId && permsCatalog && (
                <Modal onClose={() => setPermsUserId(null)} title="Board permissions" testid="user-perms-modal">
                    <p className="text-sm text-[#4A5568] mb-4">Restrict what this board member can see and do. Admins always have full access regardless of these settings.</p>
                    <div className="mb-4">
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4A5568] mb-2">Quick presets</p>
                        <div className="flex flex-wrap gap-2">
                            {(permsCatalog.presets || []).map((p) => (
                                <button
                                    key={p.slug}
                                    onClick={() => applyPreset(p.slug)}
                                    data-testid={`perms-preset-${p.slug}`}
                                    title={p.description}
                                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#F4F1EA] hover:bg-[#E53935]/10 hover:text-[#E53935] text-[#1C252E] border border-black/5 transition"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2 border-t border-black/5 pt-4 max-h-[50vh] overflow-y-auto">
                        {(permsCatalog.permissions || []).map((p) => {
                            const checked = permsSelected.includes(p.key);
                            return (
                                <label key={p.key} className="flex items-start gap-3 p-2 rounded-lg hover:bg-[#F4F1EA]/60 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => togglePerm(p.key)}
                                        data-testid={`perms-checkbox-${p.key}`}
                                        className="mt-1"
                                    />
                                    <span className="flex-1">
                                        <span className="text-sm text-[#1C252E] font-medium">{p.label}</span>
                                        <span className="text-[11px] text-[#4A5568] block">{p.key}</span>
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between mt-5">
                        <button
                            type="button"
                            onClick={() => setAuditOpen((v) => !v)}
                            data-testid="user-perms-audit-toggle"
                            className="text-xs text-[#4A5568] hover:text-[#1C252E] underline"
                        >
                            {auditOpen ? "Hide" : `Show`} change history ({permsAudit.length})
                        </button>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setPermsUserId(null)} className="text-sm font-medium text-[#4A5568]">Cancel</button>
                            <button onClick={savePerms} disabled={permsLoading} data-testid="user-perms-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60">
                                {permsLoading ? "Saving…" : "Save permissions"}
                            </button>
                        </div>
                    </div>
                    {auditOpen && (
                        <div className="mt-4 border-t border-black/5 pt-4 max-h-[40vh] overflow-y-auto" data-testid="user-perms-audit-list">
                            {permsAudit.length === 0 ? (
                                <p className="text-xs text-[#4A5568] italic">No changes recorded yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {permsAudit.map((row) => (
                                        <li key={row.id} className="text-xs bg-[#F4F1EA]/60 rounded-lg p-3" data-testid={`perms-audit-row-${row.id}`}>
                                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                                <span className="font-medium text-[#1C252E]">
                                                    {row.changed_by_email || "system"}
                                                    {row.preset_slug && (
                                                        <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-[#E53935]/10 text-[#E53935] text-[10px] uppercase tracking-wider font-semibold">{row.preset_slug}</span>
                                                    )}
                                                </span>
                                                <time className="text-[#4A5568]">{new Date(row.changed_at).toLocaleString()}</time>
                                            </div>
                                            {row.added && row.added.length > 0 && (
                                                <p className="mt-1 text-emerald-700">+ {row.added.join(", ")}</p>
                                            )}
                                            {row.removed && row.removed.length > 0 && (
                                                <p className="mt-0.5 text-[#E53935]">− {row.removed.join(", ")}</p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
};

const StatCard = ({ label, value, color = "bg-gray-100", testid }) => (
    <div className={`rounded-2xl p-4 ${color}`} data-testid={testid}>
        <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</p>
        <p className="font-heading text-2xl font-medium mt-1">{value ?? 0}</p>
    </div>
);

const SigninMethodSummary = ({ users }) => {
    const counts = users.reduce((acc, u) => {
        const key = u.last_signin_method || "never";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
    // Order in the summary — passwordless first (celebrate the rollout), password
    // last (that's the nudge target), never/signup as a subtle grey outlier.
    const order = ["passkey", "magic_link", "google", "facebook", "password", "signup", "never"];
    const total = users.length;
    return (
        <div className="bg-white border border-black/5 rounded-2xl p-4" data-testid="signin-method-summary">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4A5568] mb-2">Sign-in mix ({total} loaded)</p>
            <div className="flex flex-wrap items-center gap-2">
                {order.filter((k) => counts[k]).map((k) => {
                    const meta = SIGNIN_METHOD_META[k];
                    const Icon = meta.icon;
                    const pct = total ? Math.round((counts[k] / total) * 100) : 0;
                    return (
                        <span
                            key={k}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${meta.style}`}
                            data-testid={`signin-summary-${k}`}
                        >
                            <Icon className="w-3 h-3" strokeWidth={2.5} />
                            {meta.label} · <strong>{counts[k]}</strong> ({pct}%)
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const Modal = ({ title, children, onClose, testid }) => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-8 p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl text-[#1C252E] font-medium">{title}</h3>
                <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5" aria-label="Close"><X className="w-5 h-5 text-[#4A5568]" /></button>
            </div>
            {children}
        </div>
    </div>
);

export default UsersPanel;
